import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {CircleAlert, CircleCheck} from 'lucide-react';
import {useQuery} from '@tanstack/react-query';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageHeader from '@/shared/layout/PageHeader';
import ProviderProducts from './components/ProviderProducts';
import PatientOrdersTable, {
    type OrderSearchFields,
    type PatientOrderRow,
    type PatientOrdersSortOption,
} from './components/PatientOrdersTable';
import SalesReportChart, {type SalesReportChartProps} from './components/SalesReportChart';
import NotificationsPanel from './components/NotificationsPanel';
import SchedulingCalendar from './components/SchedulingCalendar';
import {Recommendation} from '@/shared/types/api';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {AdminService, ProviderConsoleService, UserService} from '@/shared/services';
import type {ProviderSalesReportResponse} from '@/modules/provider-console/services/providerConsoleService';
import type {ProviderOrder} from '@/shared/types/api';
import {ArrowLeft, FlaskConical} from 'lucide-react';
import Button from '@/shared/components/Button';
import {
    ProviderConsoleModules,
    ProviderConsoleModuleIds,
    ProviderConsoleModuleLabels,
    ProviderConsolePermissions,
    type ProviderConsoleModuleKey,
} from '@mld/shared/constants/providerConsoleAccess';
import ModuleDemoOverlay from './components/ModuleDemoOverlay';
import '../../styles/typography.css';
import '../../styles/colors.css';
import {decodeJwtToken, getOrganizationIdFromTokenPayload} from '@/shared/utils/tokenUtils';
import {getAdminHost} from '@/shared/utils/adminHost';
import type {StatusTone, StatusToneStyles} from '@/modules/provider-console/components/statusTone';
import {ADMIN_RETURN_STATE_KEY, ORG_PROVIDER_RETURN_TOKEN_KEY} from '@/shared/constants/providerSwitch';
import {demoNotifications as notifications} from './__demo__/notifications';

// Use centralized tone/style types shared by Provider UI components.
const statusToneStyles: StatusToneStyles = {
    success: {pill: 'text-[#2D8473]', action: 'text-[#2D8473]', icon: CircleCheck},
    warning: {pill: 'text-[#B5741B]', action: 'text-[#B5741B]'},
    danger: {pill: 'text-[#D35A73]', action: 'text-[#D35A73]', icon: CircleAlert},
    info: {pill: 'text-[#4C74A8]', action: 'text-[#4C74A8]'},
    default: {pill: '', action: ''},
};

const reportTabs = ['Last 7 Days', 'Month', 'Year', 'Custom Range'];
const PAGE_SIZES = [10, 15, 25, 50] as const;

const parsePositiveInt = (value: string | null, fallback: number): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const parsePageSize = (value: string | null, fallback: number): number => {
    const parsed = parsePositiveInt(value, fallback);
    return PAGE_SIZES.includes(parsed as (typeof PAGE_SIZES)[number]) ? parsed : fallback;
};

const isEnvFlagEnabled = (value: string | undefined): boolean => {
    if (!value) {
        return false;
    }
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
};

function formatEventDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function buildMockScheduleEvents() {
    const today = new Date();
    const todayKey = formatEventDateKey(today);
    return [
        {
            id: 1,
            title: 'Telehealth w/ Mary Jordan',
            time: '9:00 – 10:00 am CST',
            description: 'Telehealth · Johntahan Randolph',
            date: todayKey,
        },
        {
            id: 2,
            title: 'Telehealth w/ Johntahan Randolph',
            time: '12:00 – 1:15 pm CST',
            description: 'Clinic Onboarding · Argiro Argiolas',
            date: formatEventDateKey(addDays(today, 2)),
        },
        {
            id: 3,
            title: 'Staff Training',
            time: '2:30 – 4:00 pm CST',
            description: 'In-person training · Team',
            date: formatEventDateKey(addDays(today, 5)),
        },
    ];
}

// ---------------------------------------------------------------------------
// Demo-mode dummy data — shown behind blur overlay for non-engaged providers
// ---------------------------------------------------------------------------

const demoOrders: PatientOrderRow[] = (() => {
    const now = Date.now();
    const day = 86400000;
    return [
        {
            id: 90001,
            userId: null,
            patient: 'Patient A.B.',
            panel: 'Comprehensive Metabolic Panel',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 2 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 1 * day).toLocaleDateString('en-US'),
            statusLabel: 'Released',
            tone: 'success' as StatusTone,
            actionLabel: 'View Results',
        },
        {
            id: 90002,
            userId: null,
            patient: 'Patient C.D.',
            panel: 'Advanced Thyroid Panel',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 5 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 4 * day).toLocaleDateString('en-US'),
            statusLabel: 'Pending',
            tone: 'info' as StatusTone,
            actionLabel: 'View Results',
        },
        {
            id: 90003,
            userId: null,
            patient: 'Patient E.F.',
            panel: 'Wellness Panel',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 8 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 7 * day).toLocaleDateString('en-US'),
            statusLabel: 'Processing',
            tone: 'info' as StatusTone,
            actionLabel: 'View Results',
        },
        {
            id: 90004,
            userId: null,
            patient: 'Patient G.H.',
            panel: 'Biotin Clinical Panel',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 10 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 9 * day).toLocaleDateString('en-US'),
            statusLabel: 'Review',
            tone: 'warning' as StatusTone,
            actionLabel: 'View Results',
        },
        {
            id: 90005,
            userId: null,
            patient: 'Patient I.J.',
            panel: 'Hormone Panel',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 14 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 13 * day).toLocaleDateString('en-US'),
            statusLabel: 'Released',
            tone: 'success' as StatusTone,
            actionLabel: 'View Results',
        },
        {
            id: 90006,
            userId: null,
            patient: 'Patient K.L.',
            panel: 'Lipid Panel',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 18 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 17 * day).toLocaleDateString('en-US'),
            statusLabel: 'Pending',
            tone: 'info' as StatusTone,
            actionLabel: 'View Results',
        },
        {
            id: 90007,
            userId: null,
            patient: 'Patient M.N.',
            panel: 'Vitamin D Panel',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 22 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 21 * day).toLocaleDateString('en-US'),
            statusLabel: 'Released',
            tone: 'success' as StatusTone,
            actionLabel: 'View Results',
        },
        {
            id: 90008,
            userId: null,
            patient: 'Patient O.P.',
            panel: 'Complete Blood Count',
            panelType: 'VP',
            sampleCapturedDate: new Date(now - 28 * day).toLocaleDateString('en-US'),
            labReceivedDate: new Date(now - 27 * day).toLocaleDateString('en-US'),
            statusLabel: 'Processing',
            tone: 'info' as StatusTone,
            actionLabel: 'View Results',
        },
    ];
})();

const demoSalesStats: SalesReportChartProps['salesStats'] = [
    {metric: 'sales', label: 'Sales This Period', value: '$12,450'},
    {metric: 'avgDailySales', label: 'Avg Daily Sales', value: '$1,778'},
    {metric: 'orders', label: 'Orders Placed', value: '47'},
    {metric: 'conversionRate', label: 'Conversion Rate', value: '68%'},
];

const demoSalesSeries: SalesReportChartProps['salesSeries'] = (() => {
    const today = new Date();
    const amounts = [1200, 1850, 1400, 2100, 1950, 1680, 2270];
    const counts = [5, 8, 6, 9, 8, 7, 10];
    return amounts.map((salesAmount, i) => {
        const date = new Date(today);
        date.setDate(date.getDate() - (6 - i));
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return {
            date: `${date.getFullYear()}-${month}-${day}`,
            label: date.toLocaleDateString('en-US', {weekday: 'short'}),
            salesAmount,
            ordersCount: counts[i],
        };
    });
})();

const demoRecommendations: Recommendation[] = [
    {
        id: 80001,
        name: 'Comprehensive Wellness Panel',
        enabled: true,
        content: '<p>Complete health screening with 50+ biomarkers</p>',
        image: null,
        sup_type: 'Wellness',
        price: 299,
        members_price: 249,
        url: null,
        biomarkers: [],
    },
    {
        id: 80002,
        name: 'Advanced Thyroid Panel',
        enabled: true,
        content: '<p>In-depth thyroid function analysis</p>',
        image: null,
        sup_type: 'Thyroid',
        price: 149,
        members_price: 119,
        url: null,
        biomarkers: [],
    },
    {
        id: 80003,
        name: 'Hormone Health Panel',
        enabled: true,
        content: '<p>Comprehensive hormone level assessment</p>',
        image: null,
        sup_type: 'Hormones',
        price: 199,
        members_price: 169,
        url: null,
        biomarkers: [],
    },
    {
        id: 80004,
        name: 'Heart Health Panel',
        enabled: true,
        content: '<p>Cardiovascular risk assessment</p>',
        image: null,
        sup_type: 'Cardiac',
        price: 179,
        members_price: 149,
        url: null,
        biomarkers: [],
    },
];

// ---------------------------------------------------------------------------

const mapStatusToTone = (status: string | null | undefined): StatusTone => {
    if (!status) {
        return 'default';
    }

    const normalized = status.toLowerCase();
    if (normalized.includes('critical') || normalized.includes('alert') || normalized.includes('error')) {
        return 'danger';
    }
    if (normalized.includes('release') || normalized.includes('complete') || normalized.includes('ready')) {
        return 'success';
    }
    if (normalized.includes('pending') || normalized.includes('processing')) {
        return 'info';
    }
    if (normalized.includes('review')) {
        return 'warning';
    }
    return 'default';
};

const formatOrderDate = (value: string | null | undefined): string => {
    if (!value) {
        return '—';
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-US');
};

const formatPatientName = (firstName: string | null, lastName: string | null): string => {
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || 'Unknown Patient';
};

const mapOrderToDisplay = (order: ProviderOrder): PatientOrderRow => ({
    id: order.id,
    userId: order.userId ?? null,
    patient: formatPatientName(order.firstName, order.lastName),
    panel: order.oid || 'Lab Order',
    panelType: order.panelType ?? 'VP',
    sampleCapturedDate: formatOrderDate(order.sampleCapturedDate),
    labReceivedDate: formatOrderDate(order.labReceivedDate),
    sampleCapturedAt: order.sampleCapturedDate ?? null,
    labReceivedAt: order.labReceivedDate ?? null,
    statusDate: order.statusDate ?? null,
    statusLabel: order.status ?? 'Pending',
    tone: mapStatusToTone(order.status),
    actionLabel: 'View Results',
});

const isProviderOrder = (order: ProviderOrder | null): order is ProviderOrder => Boolean(order);

type RawOrderSearchResult = {
    id?: number | string | null;
    order_id?: number | string | null;
    orderID?: number | string | null;
    userId?: number | string | null;
    user_id?: number | string | null;
    userID?: number | string | null;
    oid?: number | string | null;
    firstName?: string | null;
    first_name?: string | null;
    lastName?: string | null;
    last_name?: string | null;
    status?: string | null;
    panelType?: string | null;
    panel_type?: string | null;
    createdDate?: string | number | Date | null;
    created_date?: string | number | Date | null;
    created_at?: string | number | Date | null;
    statusDate?: string | number | Date | null;
    status_date?: string | number | Date | null;
    status_updated?: string | number | Date | null;
    sampleCapturedDate?: string | number | Date | null;
    sample_captured_date?: string | number | Date | null;
    labReceivedDate?: string | number | Date | null;
    lab_received_date?: string | number | Date | null;
};

// Provider-order search results come from different legacy endpoints that don't share a strict schema.
// Normalize the field variants so the Provider UI can render consistently.
function normalizeDateToIso(value: unknown): string | null {
    if (value instanceof Date) {
        return value.toISOString();
    }
    if (typeof value === 'number') {
        const date = new Date(value);
        // Don't fall back to "now" for invalid dates; it hides data issues and breaks sorting.
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    if (typeof value === 'string') {
        const date = new Date(value);
        // Don't fall back to "now" for invalid dates; it hides data issues and breaks sorting.
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    // Unknown date types should remain unknown instead of becoming "now".
    return null;
}

function normalizeNullableDateToIso(value: unknown): string | null {
    if (value == null) return null;
    if (value === '') return null;
    return normalizeDateToIso(value);
}

const normalizeOrderFromSearch = (order: RawOrderSearchResult): ProviderOrder | null => {
    const createdDate =
        normalizeDateToIso(order.createdDate ?? order.created_date ?? order.created_at) ?? new Date(0).toISOString();
    const statusDate = normalizeNullableDateToIso(order.statusDate ?? order.status_date ?? order.status_updated);
    const sampleCapturedDate = normalizeNullableDateToIso(order.sampleCapturedDate ?? order.sample_captured_date);
    const labReceivedDate = normalizeNullableDateToIso(order.labReceivedDate ?? order.lab_received_date);

    const rawId = order.id ?? order.order_id ?? order.orderID;
    const numericId = Number(rawId);
    // Don't generate ids for rows that don't have a real order id.
    // Fake ids (e.g. Date.now()) cause unstable React keys and broken `/result/:id` links.
    if (!Number.isFinite(numericId) || numericId <= 0) {
        return null;
    }
    const id = numericId;

    const rawUserId = order.userId ?? order.user_id ?? order.userID;
    const numericUserId = Number(rawUserId);
    const userId = Number.isFinite(numericUserId) && numericUserId > 0 ? numericUserId : null;

    const oid =
        typeof order.oid === 'string'
            ? order.oid
            : order.oid != null
              ? String(order.oid)
              : rawId != null
                ? String(rawId)
                : '';

    return {
        id,
        userId,
        oid,
        firstName: order.firstName ?? order.first_name ?? '',
        lastName: order.lastName ?? order.last_name ?? '',
        status: order.status ?? '',
        panelType: order.panelType ?? order.panel_type ?? null,
        createdDate,
        statusDate,
        sampleCapturedDate,
        labReceivedDate,
    };
};

const ProviderConsole: React.FC = () => {
    const showMockData = import.meta.env.DEV;
    const showMockNotifications = showMockData || isEnvFlagEnabled(import.meta.env.VITE_PROVIDER_NOTIFICATIONS_MOCK);
    const [activeRange, setActiveRange] = useState(reportTabs[0]);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const {token} = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialPage = useMemo(() => parsePositiveInt(searchParams.get('page'), 1), [searchParams]);
    const initialPageSize = useMemo(() => parsePageSize(searchParams.get('pageSize'), 15), [searchParams]);
    const [providerOrders, setProviderOrders] = useState<ProviderOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const [page, setPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [totalCount, setTotalCount] = useState(0);
    const [searchFilters, setSearchFilters] = useState<OrderSearchFields | null>(null);
    const [sortOption, setSortOption] = useState<PatientOrdersSortOption>('recent');
    const [customRange, setCustomRange] = useState<[Date | null, Date | null]>([null, null]);
    const hasLoadedOrders = useRef(false);
    const [returnToken, setReturnToken] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        // On page refresh the token is already in sessionStorage.
        const stored = sessionStorage.getItem(ORG_PROVIDER_RETURN_TOKEN_KEY);
        if (stored) return stored;
        // On initial load the return token arrives via a short-lived
        // base-domain cookie.  Persist to sessionStorage (survives
        // refresh) and clear the cookie immediately.
        const cookies = document.cookie.split('; ');
        const returnCookie = cookies.find((c) => c.startsWith('mld_provider_return='));
        if (returnCookie) {
            const value = decodeURIComponent(returnCookie.split('=')[1] ?? '');
            sessionStorage.setItem(ORG_PROVIDER_RETURN_TOKEN_KEY, value);
            const stateCookie = cookies.find((c) => c.startsWith('mld_provider_state='));
            if (stateCookie) {
                const stateValue = decodeURIComponent(stateCookie.split('=')[1] ?? '');
                sessionStorage.setItem(ADMIN_RETURN_STATE_KEY, stateValue);
                document.cookie = 'mld_provider_state=; path=/; max-age=0';
            }
            document.cookie = 'mld_provider_return=; path=/; max-age=0';
            return value;
        }
        return null;
    });
    const [returnError, setReturnError] = useState<string | null>(null);
    const [returningToOrg, setReturningToOrg] = useState(false);
    const notificationsData = useMemo(() => (showMockNotifications ? notifications : []), [showMockNotifications]);
    const scheduleEventsData = useMemo(() => (showMockData ? buildMockScheduleEvents() : []), [showMockData]);

    const handleReturnToOrgConsole = useCallback(async () => {
        if (!returnToken) return;
        setReturnError(null);
        setReturningToOrg(true);
        try {
            let storedState: Record<string, unknown> | null = null;
            try {
                const raw = sessionStorage.getItem(ADMIN_RETURN_STATE_KEY);
                storedState = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
            } catch {
                storedState = null;
            }
            const params = new URLSearchParams();
            const fallbackOrgId = storedState?.orgId ? String(storedState.orgId) : null;
            if (fallbackOrgId) params.set('orgId', fallbackOrgId);
            if (storedState?.orgDetailTab) params.set('tab', String(storedState.orgDetailTab));
            if (storedState?.orgFilter != null) params.set('orgFilter', String(storedState.orgFilter));
            if (storedState?.providerFilter != null) {
                params.set('providerFilter', String(storedState.providerFilter));
            }
            // The org console lives on the same subdomain as the provider
            // console.  If that subdomain happens to be the admin dashboard
            // host (org has no separate subdomain), add orgConsole=1 so
            // the admin page shows the org console view.
            const adminHost = getAdminHost(window.location.hostname);
            if (window.location.hostname === adminHost) {
                params.set('orgConsole', '1');
            }
            const redirectTarget = params.toString() ? `/admin?${params.toString()}` : '/admin';
            sessionStorage.removeItem(ORG_PROVIDER_RETURN_TOKEN_KEY);
            setReturnToken(null);

            await AdminService.exchangeOrgConsoleSwitchToken(returnToken);
            window.location.assign(`${window.location.origin}${redirectTarget}`);
        } catch (error: any) {
            const message =
                error?.response?.data?.error || error?.message || 'Unable to return to org console. Please try again.';
            setReturnError(message);
            setReturningToOrg(false);
        }
    }, [returnToken]);

    const consoleScopeKey = useMemo(() => {
        if (!token) return 'anonymous';
        const payload = decodeJwtToken(token);
        if (!payload) return 'unknown';
        const orgId = getOrganizationIdFromTokenPayload(payload) ?? '';
        const roleId = payload.role_id ?? '';
        const typeId = payload.type_id ?? '';
        const impersonateId = payload.impersonate_id ?? '';
        return `${orgId}:${roleId}:${typeId}:${impersonateId}`;
    }, [token]);
    const consoleConfigQuery = useQuery({
        queryKey: ['providerConsoleConfig', consoleScopeKey],
        queryFn: () => ProviderConsoleService.getConsoleConfig(),
        enabled: Boolean(token),
        staleTime: 5 * 60 * 1000,
    });
    const moduleFlags = useMemo(() => {
        const rows = consoleConfigQuery.data?.modules ?? null;
        if (!rows || rows.length === 0) return null;
        const map = new Map<number, boolean>();
        rows.forEach((row) => {
            map.set(row.module_id, Boolean(row.enabled));
        });
        return map;
    }, [consoleConfigQuery.data?.modules]);
    const permissions = useMemo(
        () => new Set(consoleConfigQuery.data?.permissions ?? []),
        [consoleConfigQuery.data?.permissions]
    );
    const isModuleEnabled = useCallback(
        (key: ProviderConsoleModuleKey) => {
            if (!moduleFlags) return false;
            const moduleId = ProviderConsoleModuleIds[key];
            return Boolean(moduleFlags.get(moduleId));
        },
        [moduleFlags]
    );
    const canViewOrdersModule = isModuleEnabled(ProviderConsoleModules.orders);
    const canViewSalesModule = isModuleEnabled(ProviderConsoleModules.sales);
    const canViewProductsModule = isModuleEnabled(ProviderConsoleModules.products);
    const canViewOrderTests = isModuleEnabled(ProviderConsoleModules.orderTests);

    const hasOnlyOrderTestsModule = useMemo(() => {
        if (!moduleFlags) return false;
        if (!canViewOrderTests) return false;
        const otherModules = [
            ProviderConsoleModules.orders,
            ProviderConsoleModules.sales,
            ProviderConsoleModules.products,
            ProviderConsoleModules.notifications,
            ProviderConsoleModules.scheduling,
        ] as const;
        return otherModules.every((key) => !isModuleEnabled(key));
    }, [moduleFlags, canViewOrderTests, isModuleEnabled]);

    const isDemoMode = useMemo(() => {
        if (!moduleFlags) return false;
        const paidModules = [
            ProviderConsoleModules.orders,
            ProviderConsoleModules.sales,
            ProviderConsoleModules.products,
            ProviderConsoleModules.notifications,
            ProviderConsoleModules.scheduling,
        ] as const;
        return paidModules.every((key) => !isModuleEnabled(key));
    }, [moduleFlags, isModuleEnabled]);

    useEffect(() => {
        if (hasOnlyOrderTestsModule && !location.state?.fromConsoleLink) {
            navigate('/provider/order-tests', {replace: true});
        }
    }, [hasOnlyOrderTestsModule, location.state?.fromConsoleLink, navigate]);

    const canSearchOrders = permissions.has(ProviderConsolePermissions.ordersSearch);
    const canViewPii = permissions.has(ProviderConsolePermissions.ordersViewPii);

    const canFetchRecommendations = Boolean(token) && canViewProductsModule;
    const recommendationsScopeKey = useMemo(() => {
        // Include org/role/type/impersonation in the key so React Query doesn't leak cached results across users.
        if (!token) return 'anonymous';
        const payload = decodeJwtToken(token);
        if (!payload) return 'unknown';
        const orgId = getOrganizationIdFromTokenPayload(payload) ?? '';
        const roleId = payload.role_id ?? '';
        const typeId = payload.type_id ?? '';
        const impersonateId = payload.impersonate_id ?? '';
        return `${orgId}:${roleId}:${typeId}:${impersonateId}`;
    }, [token]);
    const recommendationsQuery = useQuery({
        queryKey: ['recommendations-catalog', recommendationsScopeKey],
        queryFn: () => UserService.getRecommendations(),
        enabled: canFetchRecommendations,
        staleTime: 5 * 60 * 1000,
    });

    const loadOrders = useCallback(
        async (options: {
            page: number;
            pageSize: number;
            filters: OrderSearchFields | null;
            sort: PatientOrdersSortOption;
        }) => {
            if (!canViewOrdersModule) {
                setProviderOrders([]);
                setTotalCount(0);
                return {orders: [], total: 0};
            }
            const {page: nextPage, pageSize: nextPageSize, filters, sort} = options;
            setOrdersLoading(true);
            setOrdersError(null);
            try {
                // The API resolves organization from tenant context (req.tenant.id).
                const response = filters
                    ? await ProviderConsoleService.searchPatientOrders({
                          ...filters,
                          limit: nextPageSize,
                          page: nextPage,
                          sort,
                      })
                    : await ProviderConsoleService.getPatientOrders({
                          limit: nextPageSize,
                          page: nextPage,
                          sort,
                      });

                const rawOrders = response.orders ?? [];
                const normalized = filters
                    ? rawOrders.map((order) => normalizeOrderFromSearch(order)).filter(isProviderOrder)
                    : rawOrders;

                setProviderOrders(normalized);
                setTotalCount(response.total ?? normalized.length);
                setPage(nextPage);
                setPageSize(nextPageSize);
                return {orders: normalized, total: response.total ?? normalized.length};
            } catch (error) {
                const message =
                    error instanceof Error ? error.message : 'An unexpected error occurred while loading orders.';
                setOrdersError(message);
                setProviderOrders([]);
                setTotalCount(0);
                return {orders: [], total: 0};
            } finally {
                setOrdersLoading(false);
                hasLoadedOrders.current = true;
            }
        },
        [canViewOrdersModule]
    );

    useEffect(() => {
        if (!canViewOrdersModule) {
            return;
        }
        const nextPage = parsePositiveInt(searchParams.get('page'), page);
        const nextPageSize = parsePageSize(searchParams.get('pageSize'), pageSize);

        if (!hasLoadedOrders.current) {
            void loadOrders({page: nextPage, pageSize: nextPageSize, filters: searchFilters, sort: sortOption});
            return;
        }

        if (nextPage !== page || nextPageSize !== pageSize) {
            void loadOrders({page: nextPage, pageSize: nextPageSize, filters: searchFilters, sort: sortOption});
        }
    }, [canViewOrdersModule, loadOrders, page, pageSize, searchFilters, searchParams, sortOption]);

    useEffect(() => {
        if (!canSearchOrders && searchFilters) {
            setSearchFilters(null);
        }
    }, [canSearchOrders, searchFilters]);

    const updateProviderQuery = useCallback(
        (nextPage: number, nextPageSize: number) => {
            const params = new URLSearchParams(searchParams);
            params.set('page', String(nextPage));
            params.set('pageSize', String(nextPageSize));
            setSearchParams(params, {replace: true});
        },
        [searchParams, setSearchParams]
    );

    const providerRecommendations = React.useMemo(() => {
        const recommendations = recommendationsQuery.data ?? [];
        const recommendationsMap = new Map<number, Recommendation>();

        recommendations.forEach((rec) => {
            if (rec.enabled) {
                recommendationsMap.set(rec.id, rec);
            }
        });

        return Array.from(recommendationsMap.values());
    }, [recommendationsQuery.data]);

    const patientOrders = React.useMemo(() => providerOrders.map(mapOrderToDisplay), [providerOrders]);
    const isSearchActive = Boolean(searchFilters);

    const handleSearchOrders = useCallback(
        async (fields: OrderSearchFields) => {
            const nextPage = 1;
            setSearchFilters(fields);
            updateProviderQuery(nextPage, pageSize);
            const {orders, total} = await loadOrders({
                page: nextPage,
                pageSize,
                filters: fields,
                sort: sortOption,
            });

            const droppedCount = (orders ?? []).filter((order) => !order).length;
            const toast =
                total === 0
                    ? {error: false, message: 'No orders found.'}
                    : {
                          error: false,
                          message: `${total} patient(s) found.${
                              droppedCount > 0 ? ` (${droppedCount} omitted: missing order id)` : ''
                          }`,
                      };

            return {orders: orders.map(mapOrderToDisplay), toast};
        },
        [loadOrders, pageSize, sortOption, updateProviderQuery]
    );

    const handleResetSearch = useCallback(async () => {
        setSearchFilters(null);
        updateProviderQuery(1, pageSize);
        await loadOrders({page: 1, pageSize, filters: null, sort: sortOption});
    }, [loadOrders, pageSize, sortOption, updateProviderQuery]);

    const handlePageChange = useCallback(
        async (nextPage: number) => {
            updateProviderQuery(nextPage, pageSize);
            await loadOrders({page: nextPage, pageSize, filters: searchFilters, sort: sortOption});
        },
        [loadOrders, pageSize, searchFilters, sortOption, updateProviderQuery]
    );

    const handlePageSizeChange = useCallback(
        async (nextPageSize: number) => {
            updateProviderQuery(1, nextPageSize);
            await loadOrders({page: 1, pageSize: nextPageSize, filters: searchFilters, sort: sortOption});
        },
        [loadOrders, searchFilters, sortOption, updateProviderQuery]
    );

    const handleSortChange = useCallback(
        async (nextSort: PatientOrdersSortOption) => {
            setSortOption(nextSort);
            updateProviderQuery(1, pageSize);
            await loadOrders({page: 1, pageSize, filters: searchFilters, sort: nextSort});
        },
        [loadOrders, pageSize, searchFilters, updateProviderQuery]
    );

    const salesRangeParam: ProviderSalesReportResponse['range'] = React.useMemo(() => {
        switch (activeRange) {
            case 'Month':
                return 'month';
            case 'Year':
                return 'year';
            case 'Custom Range':
                return 'custom';
            case 'Last 7 Days':
            default:
                return 'last7';
        }
    }, [activeRange]);

    const [customStart, customEnd] = customRange;
    const isCustomRange = activeRange === 'Custom Range';

    const formatDateParam = (date: Date) => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const customFrom = customStart ? formatDateParam(customStart) : undefined;
    const customTo = customEnd ? formatDateParam(customEnd) : undefined;
    const canFetchCustomRange = Boolean(customFrom && customTo);

    const salesQuery = useQuery({
        queryKey: ['providerSalesReport', salesRangeParam, customFrom ?? null, customTo ?? null],
        queryFn: () =>
            ProviderConsoleService.getSalesReport({
                range: salesRangeParam,
                ...(salesRangeParam === 'custom' && canFetchCustomRange ? {from: customFrom, to: customTo} : {}),
            }),
        enabled: canViewSalesModule && (salesRangeParam !== 'custom' || canFetchCustomRange),
    });

    const reportSeries = useMemo(
        () => (isCustomRange && !canFetchCustomRange ? [] : (salesQuery.data?.series ?? [])),
        [canFetchCustomRange, isCustomRange, salesQuery.data?.series]
    );
    const formatCurrency = (value: number) => value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});

    const salesStats = React.useMemo<SalesReportChartProps['salesStats']>(() => {
        const data = salesQuery.data;
        if (!data || (isCustomRange && !canFetchCustomRange)) {
            return [
                {metric: 'sales', label: 'Sales This Period', value: '—'},
                {metric: 'avgDailySales', label: 'Avg Daily Sales', value: '—'},
                {metric: 'orders', label: 'Orders Placed', value: '—'},
                {metric: 'conversionRate', label: 'Conversion Rate', value: '—'},
            ];
        }

        const conversionRate =
            typeof data.conversionRate === 'number' && Number.isFinite(data.conversionRate)
                ? `${Math.round(data.conversionRate * 100)}%`
                : '—';

        return [
            {metric: 'sales', label: 'Sales This Period', value: formatCurrency(data.salesThisPeriod)},
            {metric: 'avgDailySales', label: 'Avg Daily Sales', value: formatCurrency(data.avgDailySales)},
            {metric: 'orders', label: 'Orders Placed', value: `${data.ordersPlaced}`},
            {metric: 'conversionRate', label: 'Conversion Rate', value: conversionRate},
        ];
    }, [canFetchCustomRange, isCustomRange, salesQuery.data]);
    const returnTo = `${location.pathname}${location.search}`;

    const moduleRegistry = useMemo(
        () => [
            {
                key: ProviderConsoleModules.orders as ProviderConsoleModuleKey,
                area: 'main' as const,
                content: isDemoMode ? (
                    <PatientOrdersTable
                        orders={demoOrders}
                        ordersLoading={false}
                        ordersError={null}
                        statusToneStyles={statusToneStyles}
                        onSearchOrders={async () => ({orders: [], toast: {error: false, message: ''}})}
                        onResetSearch={() => {}}
                        page={1}
                        pageSize={15}
                        totalCount={demoOrders.length}
                        onPageChange={() => {}}
                        onPageSizeChange={() => {}}
                        sortOption="recent"
                        onSortChange={() => {}}
                        isSearchActive={false}
                        canSearch={false}
                        canViewPii={false}
                    />
                ) : (
                    <PatientOrdersTable
                        orders={patientOrders}
                        ordersLoading={ordersLoading}
                        ordersError={ordersError}
                        statusToneStyles={statusToneStyles}
                        onSearchOrders={handleSearchOrders}
                        onResetSearch={handleResetSearch}
                        page={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        sortOption={sortOption}
                        onSortChange={handleSortChange}
                        isSearchActive={isSearchActive}
                        returnTo={returnTo}
                        canSearch={canSearchOrders}
                        canViewPii={canViewPii}
                    />
                ),
            },
            {
                key: ProviderConsoleModules.sales as ProviderConsoleModuleKey,
                area: 'main' as const,
                content: isDemoMode ? (
                    <SalesReportChart
                        reportTabs={reportTabs}
                        activeRange="Last 7 Days"
                        onSelectRange={() => {}}
                        salesStats={demoSalesStats}
                        salesSeries={demoSalesSeries}
                        conversionRate={0.68}
                        customRange={[null, null]}
                        onChangeCustomRange={() => {}}
                    />
                ) : (
                    <SalesReportChart
                        reportTabs={reportTabs}
                        activeRange={activeRange}
                        onSelectRange={setActiveRange}
                        salesStats={salesStats}
                        salesSeries={reportSeries}
                        conversionRate={
                            isCustomRange && !canFetchCustomRange ? null : (salesQuery.data?.conversionRate ?? null)
                        }
                        customRange={customRange}
                        onChangeCustomRange={setCustomRange}
                    />
                ),
            },
            {
                key: ProviderConsoleModules.products as ProviderConsoleModuleKey,
                area: 'main' as const,
                content: isDemoMode ? (
                    <div>
                        <ProviderProducts recommendations={demoRecommendations} loading={false} />
                    </div>
                ) : (
                    <div>
                        <ProviderProducts
                            recommendations={providerRecommendations}
                            loading={
                                !canFetchRecommendations ||
                                recommendationsQuery.isLoading ||
                                recommendationsQuery.isFetching
                            }
                        />
                    </div>
                ),
            },
            {
                key: ProviderConsoleModules.notifications as ProviderConsoleModuleKey,
                area: 'side' as const,
                content: (
                    <NotificationsPanel
                        notifications={isDemoMode ? notifications : notificationsData}
                        statusToneStyles={statusToneStyles}
                    />
                ),
            },
            {
                key: ProviderConsoleModules.scheduling as ProviderConsoleModuleKey,
                area: 'side' as const,
                content: (
                    <SchedulingCalendar
                        selectedDate={selectedDate}
                        onChangeSelectedDate={isDemoMode ? () => {} : setSelectedDate}
                        events={isDemoMode ? buildMockScheduleEvents() : scheduleEventsData}
                    />
                ),
            },
        ],
        [
            activeRange,
            canFetchCustomRange,
            canFetchRecommendations,
            canSearchOrders,
            canViewPii,
            customRange,
            handlePageChange,
            handlePageSizeChange,
            handleResetSearch,
            handleSearchOrders,
            handleSortChange,
            isCustomRange,
            isDemoMode,
            isSearchActive,
            notificationsData,
            ordersError,
            ordersLoading,
            page,
            pageSize,
            patientOrders,
            providerRecommendations,
            recommendationsQuery.isFetching,
            recommendationsQuery.isLoading,
            reportSeries,
            returnTo,
            salesQuery.data?.conversionRate,
            salesStats,
            scheduleEventsData,
            selectedDate,
            sortOption,
            totalCount,
        ]
    );

    const mainModules = moduleRegistry.filter((m) => m.area === 'main' && (isDemoMode || isModuleEnabled(m.key)));
    const sideModules = moduleRegistry.filter((m) => m.area === 'side' && (isDemoMode || isModuleEnabled(m.key)));

    // Don't render the console while we're about to redirect to order-tests.
    if (hasOnlyOrderTestsModule && !location.state?.fromConsoleLink) {
        return null;
    }

    const headerAction = returnToken ? (
        <Button
            variant="secondary"
            size="md"
            onClick={handleReturnToOrgConsole}
            disabled={returningToOrg}
            iconLeft={<ArrowLeft className="h-3.5 w-3.5" />}>
            {returningToOrg ? 'Returning...' : 'Back to Org Console'}
        </Button>
    ) : null;

    return (
        <DashboardLayout headerAction={headerAction}>
            <div className="relative">
                <div className="">
                    {returnError ? <div className="mb-4 text-sm text-mld-danger">{returnError}</div> : null}
                    <div className="flex items-center justify-between">
                        <PageHeader title="Provider Console" subtitle="Operational patient overview" className="my-0" />
                        {canViewOrderTests && (
                            <button
                                type="button"
                                onClick={() => navigate('/provider/order-tests')}
                                className="inline-flex items-center gap-2 rounded-lg bg-mld-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-mld-primary/90 transition-colors">
                                <FlaskConical className="h-4 w-4" />
                                Order Tests
                            </button>
                        )}
                    </div>

                    <div className="mt-8 grid gap-8 lg:grid-cols-[2.2fr_1fr] max-w-full overflow-hidden">
                        <div className="space-y-8 min-w-0">
                            {mainModules.map((module) => (
                                <React.Fragment key={module.key}>
                                    {isDemoMode ? (
                                        <ModuleDemoOverlay moduleName={ProviderConsoleModuleLabels[module.key]}>
                                            {module.content}
                                        </ModuleDemoOverlay>
                                    ) : (
                                        module.content
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="space-y-8 min-w-0">
                            {sideModules.map((module) => (
                                <React.Fragment key={module.key}>
                                    {isDemoMode ? (
                                        <ModuleDemoOverlay moduleName={ProviderConsoleModuleLabels[module.key]}>
                                            {module.content}
                                        </ModuleDemoOverlay>
                                    ) : (
                                        module.content
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ProviderConsole;
